/* Frequency & Stone Quick Add: add to the real Shopify cart without leaving
   the current collection or homepage.

   There are three Quick Add entry points and they are not wrapped the same way:

     snippets/quick-add.liquid            inside <product-form-component>
     snippets/fs-selected-piece-card      bare {% form 'product' %}
     sections/fs-static-match-result      bare <form action="/cart/add">

   Only the first is owned by Horizon. Horizon registers its own document-level
   capture listener (component.js) and registers it before this file, so a
   capture listener here cannot pre-empt it — stopImmediatePropagation runs after
   Horizon has already POSTed. Intercepting that form therefore added every piece
   twice: one POST from ProductFormComponent.handleSubmit, one from here.

   So this file no longer adds anything Horizon already owns. It intercepts only
   the two bare forms, which would otherwise do a full-page POST, and every
   entry point now reports through the one channel the theme already listens to:
   the standard cart update event. That event is also what the drawer, the cart
   icon and the cart items component subscribe to, so there is a single sync path
   rather than two racing ones.

   The cart deliberately does not open on add. That is the theme's
   settings.auto_open_cart_drawer, which is off — not something this file forces. */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const cartUrl = `${ROOT}cart.js`;
  const addUrl = `${ROOT}cart/add.js`;
  const feedbackDuration = 2600;

  // StandardEvents.cartLinesUpdate from @shopify/events. Spelled out as a string
  // so this stays a classic script: importing the module would change when the
  // submit listener below is registered, and registration order is exactly what
  // this file gets wrong when it fights Horizon.
  const CART_UPDATE_EVENT = 'shopify:cart:lines-update';

  const updateHeaderCount = (itemCount) => {
    document.querySelectorAll('.fs-nav-action[aria-controls="cart-drawer"], .fs-nav-action[href*="/cart"]').forEach((action) => {
      let count = action.querySelector('.fs-cart-count');
      if (itemCount > 0 && !count) {
        count = document.createElement('span');
        count.className = 'fs-cart-count';
        action.append(count);
      }
      if (count) {
        count.textContent = String(itemCount);
        count.hidden = itemCount === 0;
      }
    });
  };

  const showToast = (title, itemCount) => {
    let toast = document.querySelector('.fs-quick-add-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fs-quick-add-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.append(toast);
    }
    toast.textContent = itemCount === '' ? title : `${title} added to bag · ${itemCount}`;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), feedbackDuration);
  };

  const setButtonFeedback = (button, added) => {
    if (!button) return;
    const text = button.querySelector('.add-to-cart-text__content > span > span');
    const target = text || button;
    if (added) {
      button.dataset.fsQuickAddState = 'added';
      button.disabled = true;
      target.dataset.fsQuickAddOriginal = target.textContent;
      target.textContent = 'ADDED ✓';
      window.setTimeout(() => {
        target.textContent = target.dataset.fsQuickAddOriginal || 'QUICK ADD +';
        delete target.dataset.fsQuickAddOriginal;
        delete button.dataset.fsQuickAddState;
        button.disabled = false;
      }, 1600);
    } else {
      button.disabled = false;
      delete button.dataset.fsQuickAddState;
    }
  };

  const pieceTitle = (form, marker) => marker?.dataset.fsQuickAddTitle
    || form.closest('quick-add-component')?.dataset.productTitle
    || form.closest('.fs-selected-piece-card')?.querySelector('.fs-selected-piece-card__title')?.textContent?.trim()
    || 'Piece';

  // Which card is mid-add, so the toast can name the piece and the button can be
  // released when the cart event lands. Cleared as soon as it is consumed.
  let pendingAdd = null;

  // A cart mutation can finish before an earlier /cart.js request returns.
  // Keep one monotonically increasing sequence so a stale response can never
  // paint the F&S header with an older item count.
  let latestCartSync = 0;

  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const marker = form?.querySelector('[data-fs-quick-add-form]');
    if (!marker) return;

    const button = event.submitter instanceof HTMLButtonElement
      ? event.submitter
      : form.querySelector('button[type="submit"]');
    pendingAdd = { title: pieceTitle(form, marker), button };

    // Horizon owns this form -- but only once <product-form-component> has been
    // defined. Adding here as well after that point is the double-add.
    //
    // Before the definition lands nothing is intercepting the submit, so a click
    // in that window falls through to a native POST to /cart/add and the browser
    // leaves the page for the cart. That is the "first quick add jumps to the
    // bag, second one behaves" report: product-form.js is an ES module whose
    // graph resolves later than this classic `defer` script, so the gap is real
    // on every cold load and closes for good once the module has run.
    //
    // customElements.get() is the right probe: element upgrade is synchronous at
    // definition time, so a defined name means this instance is already live.
    if (form.closest('product-form-component') && customElements.get('product-form-component')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.fsQuickAddSubmitting === 'true') return;
    form.dataset.fsQuickAddSubmitting = 'true';

    try {
      if (button) button.disabled = true;
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: new FormData(form),
      });
      const added = await addResponse.json();
      if (!addResponse.ok || added.status) throw new Error(added.description || added.message || 'Unable to add this piece');

      // Report through the theme's own channel instead of updating the header
      // here, so a bare form and a Horizon form settle along the same path.
      // No sections are attached: cart-items-component falls back to its own
      // section render, which is correct because this is the only event for
      // this add and nothing races it.
      const cartEvent = new CustomEvent(CART_UPDATE_EVENT, { bubbles: true, composed: true });
      cartEvent.action = 'add';
      cartEvent.promise = Promise.resolve({ detail: {} });
      document.dispatchEvent(cartEvent);

      document.dispatchEvent(new CustomEvent('fs:quick-add:success', { detail: { item: added } }));
    } catch (error) {
      pendingAdd = null;
      setButtonFeedback(button, false);
      showToast(error instanceof Error ? error.message : 'Unable to add this piece', '');
    } finally {
      delete form.dataset.fsQuickAddSubmitting;
    }
  }, true);

  // The one place the F&S bag count is refreshed. Covers Quick Add, the PDP
  // add-to-bag, and drawer removals and quantity changes, none of which the
  // header knew about before — only a page reload re-rendered it server-side.
  document.addEventListener(CART_UPDATE_EVENT, (event) => {
    const sync = ++latestCartSync;
    // Horizon supplies the authoritative cart with its event promise. Our two
    // small custom mutations only supply an empty detail object, in which case
    // we read the cart after the mutation has settled.
    const settled = event.promise ? Promise.resolve(event.promise).catch(() => null) : Promise.resolve(null);
    settled.then(async (result) => {
      if (sync !== latestCartSync) return;
      // Answer on the button the moment the add is known to have worked. The
      // cart read below is a second round trip, and waiting for it put ADDED
      // roughly two seconds after the press — long enough that the label had
      // already settled back to ADD TO BAG first, so the confirmation looked
      // like a retry. The count and the toast can arrive late; the button cannot.
      const pending = event.action === 'add' ? pendingAdd : null;
      if (pending) {
        pendingAdd = null;
        setButtonFeedback(pending.button, true);
      }

      const eventCount = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
      if (Number.isFinite(eventCount)) {
        updateHeaderCount(eventCount);
        if (pending) showToast(pending.title, eventCount);
        return;
      }

      try {
        const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        // A newer cart event won while this fallback request was in flight.
        if (sync !== latestCartSync) return;
        updateHeaderCount(cart.item_count);
        if (pending) showToast(pending.title, cart.item_count);
      } catch (error) {
        // The next cart event or page load will re-read the server-rendered count.
      }
    });
  });
})();
